// ItemsService.tsx
import { BaseService } from '../../core/BaseService';
import { ChangeStatusRequest, PaginationParams } from '../../type';
import {
  AssignPriceListRequest,
  AssignStoreRequest,
  AssignUnassignAllRequest,
  AssignUnassignAllResponse,
  AssignUnassignModelRequest,
  AssignUnassignResponse,
  AssignUnassignVendorRequest,
  AssignUnitRequest,
  ChangeStatusItemsResponse,
  DeleteItemsResponse,
  GetItemByIdResponse,
  GetItemHistoryResponse,
  GetItemLocationResponse,
  GetItemModelResponse,
  GetItemPriceListResponse,
  GetItemStoreResponse,
  GetItemUnitResponse,
  GetItemVendorResponse,
  GetItemsDropdownResponse,
  GetItemsResponse,
  ItemExcelUpdatePreviewResponse,
  ItemExcelUpdateResponse,
  ItemsRequest,
  ItemsResponse,
  UnassignPriceListRequest,
  UnassignStoreRequest,
  UnassignUnitRequest,
} from './interface';

export class ItemsProfile extends BaseService {
  async createItems(req: ItemsRequest) {
    return this.post<ItemsResponse, ItemsRequest>(`${this.apiUrl}/api/items`, req);
  }

  async getItems(params: PaginationParams) {
    return this.get<GetItemsResponse>(`${this.apiUrl}/api/items/paged`, params);
  }

  async getItemsUngroup(params: PaginationParams) {
    return this.get<GetItemsResponse>(`${this.apiUrl}/api/items/ungroup`, params);
  }

  async getItemsByGroup(params: PaginationParams) {
    return this.get<GetItemsResponse>(`${this.apiUrl}/api/items/bygroup`, params);
  }

  async getItemsDropdown(params: PaginationParams & { itemGroupId?: number }) {
    return this.get<GetItemsDropdownResponse>(`${this.apiUrl}/api/items/get`, params);
  }

  async updateItems(id: number, req: ItemsRequest) {
    return this.put<ItemsResponse, ItemsRequest>(`${this.apiUrl}/api/items/${id}`, req);
  }

  async deleteItems(id: number) {
    return this.delete<DeleteItemsResponse>(`${this.apiUrl}/api/items/${id}`);
  }

  async changeStatusItems(id: number, active: string, req: ChangeStatusRequest) {
    return this.put<ChangeStatusItemsResponse, ChangeStatusRequest>(`${this.apiUrl}/api/items/${active}/${id}`, req);
  }

  async getItemUnit(params: PaginationParams & { itemId: number }) {
    return this.get<GetItemUnitResponse>(`${this.apiUrl}/api/units/byitemid`, params);
  }

  async assignUnit(req: AssignUnitRequest) {
    return this.post<AssignUnassignResponse, AssignUnitRequest>(`${this.apiUrl}/api/items/unit/assign`, req);
  }

  async unassignUnit(req: UnassignUnitRequest) {
    return this.put<AssignUnassignResponse, UnassignUnitRequest>(`${this.apiUrl}/api/items/unit/unassign`, req);
  }

  async getItemModel(params: PaginationParams & { itemId: number }) {
    return this.get<GetItemModelResponse>(`${this.apiUrl}/api/models/byitemid`, params);
  }

  async assignModel(req: AssignUnassignModelRequest) {
    return this.post<AssignUnassignResponse, AssignUnassignModelRequest>(`${this.apiUrl}/api/items/model/assign`, req);
  }

  async unassignModel(req: AssignUnassignModelRequest) {
    return this.put<AssignUnassignResponse, AssignUnassignModelRequest>(`${this.apiUrl}/api/items/model/unassign`, req);
  }

  async assignAllModels(req: AssignUnassignAllRequest) {
    return this.post<AssignUnassignAllResponse, AssignUnassignAllRequest>(
      `${this.apiUrl}/api/items/model/assignall`,
      req
    );
  }

  async unassignAllModels(req: AssignUnassignAllRequest) {
    return this.put<AssignUnassignAllResponse, AssignUnassignAllRequest>(
      `${this.apiUrl}/api/items/model/unassignall`,
      req
    );
  }

  async getItemVendor(params: PaginationParams & { itemId: number }) {
    return this.get<GetItemVendorResponse>(`${this.apiUrl}/api/vendors/byitemid`, params);
  }

  async assignVendor(req: AssignUnassignVendorRequest) {
    return this.post<AssignUnassignResponse, AssignUnassignVendorRequest>(
      `${this.apiUrl}/api/items/vendor/assign`,
      req
    );
  }

  async unassignVendor(req: AssignUnassignVendorRequest) {
    return this.put<AssignUnassignResponse, AssignUnassignVendorRequest>(
      `${this.apiUrl}/api/items/vendor/unassign`,
      req
    );
  }

  async assignAllVendors(req: AssignUnassignAllRequest) {
    return this.post<AssignUnassignAllResponse, AssignUnassignAllRequest>(
      `${this.apiUrl}/api/items/vendor/assignall`,
      req
    );
  }

  async unassignAllVendors(req: AssignUnassignAllRequest) {
    return this.put<AssignUnassignAllResponse, AssignUnassignAllRequest>(
      `${this.apiUrl}/api/items/vendor/unassignall`,
      req
    );
  }

  async getItemPriceList(params: PaginationParams & { itemId: number }) {
    return this.get<GetItemPriceListResponse>(`${this.apiUrl}/api/pricelists/byitemid`, params);
  }

  async assignPriceList(req: AssignPriceListRequest) {
    return this.post<AssignUnassignResponse, AssignPriceListRequest>(`${this.apiUrl}/api/items/pricelist/assign`, req);
  }

  async unassignPriceList(req: UnassignPriceListRequest) {
    return this.put<AssignUnassignResponse, UnassignPriceListRequest>(
      `${this.apiUrl}/api/items/pricelist/unassign`,
      req
    );
  }

  async getItemStore(params: PaginationParams & { itemId: number }) {
    return this.get<GetItemStoreResponse>(`${this.apiUrl}/api/stores/byitemid`, params);
  }

  async assignStore(req: AssignStoreRequest) {
    return this.post<AssignUnassignResponse, AssignStoreRequest>(`${this.apiUrl}/api/stores/item/assign`, req);
  }

  async unassignStore(req: UnassignStoreRequest) {
    return this.put<AssignUnassignResponse, UnassignStoreRequest>(`${this.apiUrl}/api/stores/item/unassign`, req);
  }

  async getItemByID(id: number) {
    return this.get<GetItemByIdResponse>(`${this.apiUrl}/api/items/${id}`);
  }

  async getItemLocation(storeId: number, itemId: number) {
    return this.get<GetItemLocationResponse>(`${this.apiUrl}/api/items/get/location`, {
      storeId,
      itemId,
    });
  }

  async getItemHistory(itemId: number, params: PaginationParams) {
    return this.get<GetItemHistoryResponse>(`${this.apiUrl}/api/items/item-history/${itemId}`, params);
  }

  async exportItemsBySelectedIds(ids: number[]) {
    return this.post<Blob, number[]>(`${this.apiUrl}/api/items/export/byselectedids`, ids, {
      responseType: 'blob',
    });
  }

  async readItemsForUpdate(file: File) {
    const headers = { 'Content-Type': 'multipart/form-data' };
    const formData = new FormData();
    formData.append('file', file);
    return this.post<ItemExcelUpdatePreviewResponse, FormData>(
      `${this.apiUrl}/api/items/readforupdate`,
      formData,
      { headers },
    );
  }

  async updateItemsFromExcel(rows: unknown) {
    return this.post<ItemExcelUpdateResponse, unknown>(
      `${this.apiUrl}/api/items/updatefromexcel`,
      rows,
    );
  }
}
